import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useProductService } from 'services/products';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getCacheKeyWithCompany } from 'utils/cacheUtils';
import { Product } from 'types/product';

const PRODUCTS_CACHE_KEY_BASE = 'products_cache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

const getProductsCacheKey = (
  showArchived: boolean,
  companyId: string | null | undefined,
) => {
  const baseKey = `${PRODUCTS_CACHE_KEY_BASE}_${showArchived ? 'withArchived' : 'withoutArchived'}`;
  return getCacheKeyWithCompany(baseKey, companyId);
};

const sortProductsByDateDesc = (products: Product[]) =>
  [...products].sort((a, b) => {
    const aTime = new Date(a.updatedAt).getTime();
    const bTime = new Date(b.updatedAt).getTime();

    if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
      return 0;
    }

    return bTime - aTime;
  });

interface UseProductsDataOptions {
  showArchived: boolean;
}

interface UseProductsDataReturn {
  products: Product[];
  loading: boolean;
  reloading: boolean;
  error: string | null;
  reload: () => void;
  updateProducts: (products: Product[]) => void;
}

export function useProductsData(
  options: UseProductsDataOptions,
): UseProductsDataReturn {
  const { showArchived } = options;
  const productService = useProductService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  // Create cache key with company ID suffix
  const productsCacheKey = useMemo(
    () => getProductsCacheKey(showArchived, activeCompanyId),
    [showArchived, activeCompanyId],
  );

  // Use localStorage hook with encryption and expiration
  const {
    data: cachedProducts,
    setData: setCachedProducts,
    isInitialized: cacheInitialized,
  } = useLocalStorage<Product[]>({
    key: productsCacheKey,
    expirationMs: CACHE_EXPIRATION_MS,
    encrypt: true,
  });

  const fetchProducts = useCallback(
    async (isReload = false, hasCachedData = false) => {
      // Don't fetch if company is not initialized yet
      if (!isCompanyInitialized || !activeCompanyId) {
        return;
      }

      // Fetch fresh data (either cache expired, doesn't exist, or isReload is true)
      if (isReload) {
        setReloading(true);
      } else {
        // Only set loading if we don't have cached data to show
        if (!hasCachedData) {
          setLoading(true);
        }
      }
      setError(null);

      try {
        // Fetch both products and drafts in parallel
        const [productsData, draftsData] = await Promise.all([
          productService.getAllProducts(showArchived),
          productService.getAllDraftProducts(),
        ]);

        // Combine products and drafts, then sort by updatedAt (newest first)
        const allProducts = [...productsData, ...draftsData];
        const sortedProducts = sortProductsByDateDesc(allProducts);
        setProducts(sortedProducts);

        // Limit cached products to first 20 to avoid large data issues
        const productsToCache =
          sortedProducts.length > 20
            ? sortedProducts.slice(0, 20)
            : sortedProducts;

        // Save to cache with encryption
        await setCachedProducts(productsToCache);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch products:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load products. Please check your connection.';
        setError(errorMessage);
      } finally {
        if (isReload) {
          setReloading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      showArchived,
      productService,
      setCachedProducts,
      isCompanyInitialized,
      activeCompanyId,
    ],
  );

  // Reset when showArchived or company changes (cache key changes)
  useEffect(() => {
    setLoading(true);
    setProducts([]);
    setError(null);
    hasFetchedRef.current = false;
  }, [showArchived, activeCompanyId]);

  // Load cached data on mount or when company/cache key changes
  useEffect(() => {
    if (!cacheInitialized || !isCompanyInitialized) return; // Wait for both to be ready
    if (hasFetchedRef.current) return; // Prevent multiple fetches for same company/cache key

    hasFetchedRef.current = true;

    if (cachedProducts) {
      // Use cached data - it's still valid (hook already checked expiration)
      setProducts(sortProductsByDateDesc(cachedProducts));
      setLoading(false);
      // Always fetch fresh data in the background even if cache exists
      void fetchProducts(false, true);
    } else {
      // No cache or expired - fetch data immediately
      void fetchProducts(false, false);
    }
  }, [
    cacheInitialized,
    isCompanyInitialized,
    productsCacheKey, // Use cache key instead of individual dependencies to detect company changes
    cachedProducts,
    fetchProducts,
  ]);

  const reload = useCallback(() => {
    void fetchProducts(true);
  }, [fetchProducts]);

  const updateProducts = useCallback(
    (updatedProducts: Product[]) => {
      const sortedProducts = sortProductsByDateDesc(updatedProducts);
      setProducts(sortedProducts);

      // Update cache with encrypted data (fire and forget)
      void setCachedProducts(sortedProducts);
    },
    [setCachedProducts],
  );

  return {
    products,
    loading,
    reloading,
    error,
    reload,
    updateProducts,
  };
}
